/**
 * Sales Recap (`sales-recap`): finance's monthly recap workbook as one page.
 *
 * The workbook's slicers are the report's filters (the bar above, toggle
 * buttons like the slicers); its fifteen tabs are two views of one payload:
 *
 * * **Overview** -- the three overview tabs in one (Business and Sales group are
 *   filters): product lines, the top and bottom five against last year and
 *   against plan, ranked by end user or by ship-to (the Food Service tab's way),
 *   and end users opening to their ship-tos.
 * * **Detail** -- the four pivot tabs per business as one table with a Rows
 *   switch: End user, End user > Ship-to > SKU, End user > Product line >
 *   Ship-to, SKU > Ship-to.
 *
 * With "Janus: shown separately", Janus business is out of every figure above
 * and reported at the foot by customer -- opening and follow-on shipments,
 * split at five or more of the nine SKUs, as the team is comped.
 */

import { Fragment, useMemo, useState } from 'react'

import type { CustomReportProps } from '..'
import { Card, Empty } from '../shared/Card'
import { dataset, text } from '../shared/rows'
import type { Keyed } from '../shared/rows'
import { CompareTable } from './CompareTable'
import type { Labels, TableRow } from './CompareTable'
import { amount, percent, tone } from './format'
import { GoGets } from './GoGets'
import { MonthByMonth } from './MonthByMonth'
import { buildTree, figuresOf, LAYOUTS, sum, visible } from './tree'
import type { Figures, TreeNode } from './tree'

type View = 'overview' | 'detail' | 'months' | 'go_gets'

const VIEWS: readonly { key: View; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'detail', label: 'Detail' },
  { key: 'months', label: 'Month by month' },
  { key: 'go_gets', label: 'Go Gets' },
]

const END_USER_SHIP_TO = ['end_user', 'ship_to'] as const

function num(value: Keyed[string] | undefined): number {
  return typeof value === 'number' ? value : Number(value ?? 0)
}

export function SalesRecap({ result, values }: CustomReportProps) {
  const [view, setView] = useState<View>('overview')
  const header = dataset(result, 'header')[0]
  const lines = useMemo(() => dataset(result, 'lines'), [result])
  const monthLines = useMemo(() => dataset(result, 'month_lines'), [result])
  if (header === undefined) return <Empty label="This run carried no recap" />

  const money = values['unit'] === 'fob'
  const labels: Labels = {
    cy: text(header['fy_label']) ?? 'This year',
    py: text(header['py_label']) ?? 'Prior year',
    plan: text(header['plan_label']) ?? 'Plan',
    money,
  }
  const totals = dataset(result, 'totals')[0]
  const total = totals === undefined ? sum([]) : figuresOf(totals)
  const separate = text(header['janus_mode']) === 'separate'
  // Fiscal months in view, as 0-based indexes (0 = October).
  const monthsShown = (text(header['months_shown']) ?? '')
    .split(',')
    .filter((part) => part !== '')
    .map((part) => Number(part) - 1)

  return (
    <div className="bespoke-report srp-page rcp">
      <section className="rcp-banner">
        <p className="rcp-banner-title">
          {labels.cy} vs {labels.py} · {text(header['unit_label'])}
        </p>
        <p className="srp-period">
          {text(header['period_label'])}
          {header['actuals_through'] !== null &&
            ` · actuals through ${monthName(text(header['actuals_through']))}`}
        </p>
        <p className="srp-period">
          {labels.plan} basis: {text(header['basis'])}
        </p>
      </section>

      <Tiles total={total} labels={labels} />

      <div className="rcp-tabs" role="tablist" aria-label="View">
        {VIEWS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={view === key ? 'rcp-tab is-active' : 'rcp-tab'}
            onClick={() => {
              setView(key)
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'overview' && <Overview result={result} lines={lines} labels={labels} total={total} />}
      {view === 'detail' && <Detail lines={lines} labels={labels} total={total} />}
      {view === 'months' && <MonthByMonth lines={monthLines} labels={labels} monthsShown={monthsShown} />}
      {view === 'go_gets' && (
        <GoGets rows={dataset(result, 'go_gets')} planLabel={labels.plan} fyLabel={labels.cy} money={money} />
      )}

      {separate && <Janus result={result} money={money} />}
    </div>
  )
}

function monthName(iso: string | null): string {
  if (iso === null) return ''
  const [year, month] = iso.split('-')
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${names[Number(month) - 1] ?? ''} ${year ?? ''}`
}

function Tiles({ total, labels }: { total: Figures; labels: Labels }) {
  const vsPy = total.cy - total.py
  const vsPlan = total.cy - total.plan
  const pct = (base: number, diff: number) => (base === 0 ? null : (diff / Math.abs(base)) * 100)
  return (
    <section className="srp-kpis" aria-label="Totals">
      <div className="srp-kpi">
        <p className="srp-kpi-label">{labels.cy}</p>
        <p className="srp-kpi-value numeric">{amount(total.cy, labels.money)}</p>
      </div>
      <div className="srp-kpi">
        <p className="srp-kpi-label">vs {labels.py}</p>
        <p className={`srp-kpi-value numeric ${tone(vsPy)}`}>{amount(vsPy, labels.money)}</p>
        <p className="srp-kpi-sub">
          {percent(pct(total.py, vsPy))} · {labels.py} {amount(total.py, labels.money)}
        </p>
      </div>
      <div className="srp-kpi">
        <p className="srp-kpi-label">vs {labels.plan}</p>
        <p className={`srp-kpi-value numeric ${tone(vsPlan)}`}>{amount(vsPlan, labels.money)}</p>
        <p className="srp-kpi-sub">
          {percent(pct(total.plan, vsPlan))} · {labels.plan} {amount(total.plan, labels.money)}
        </p>
      </div>
    </section>
  )
}

// --- Overview --------------------------------------------------------------------------

function Overview({
  result,
  lines,
  labels,
  total,
}: {
  result: CustomReportProps['result']
  lines: readonly Keyed[]
  labels: Labels
  total: Figures
}) {
  const [rankBy, setRankBy] = useState<'end_user' | 'ship_to'>('end_user')
  const productLines: TableRow[] = [
    ...dataset(result, 'product_lines').map((row) => ({
      id: text(row['product_line']) ?? '',
      label: text(row['product_line']) ?? '',
      figures: figuresOf(row),
    })),
    { id: 'total', label: 'Grand total', figures: total, total: true },
  ]
  const ranking = dataset(result, 'rankings').filter((row) => text(row['by']) === rankBy)
  const noun = rankBy === 'end_user' ? 'End users' : 'Ship-tos'

  return (
    <>
      <Card
        title="Product line performance"
        subtitle={`Sorted by variance against ${labels.py}, largest gain first.`}
      >
        {productLines.length > 1 ? (
          <CompareTable first="Product line" rows={productLines} labels={labels} />
        ) : (
          <Empty label="Nothing in these filters" />
        )}
      </Card>

      <Card
        title={`Top and bottom 5 ${noun.toLowerCase()}`}
        subtitle={`Ranked by the variance where both sides have volume, as in the workbook: a ${
          rankBy === 'end_user' ? 'customer' : 'ship-to'
        } new or gone this year is not on these lists.`}
        actions={
          <div className="rcp-switch" role="group" aria-label="Rank by">
            {(['end_user', 'ship_to'] as const).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={rankBy === key}
                className={rankBy === key ? 'srp-chip is-on' : 'srp-chip'}
                onClick={() => {
                  setRankBy(key)
                }}
              >
                {key === 'end_user' ? 'End user' : 'Ship-to'}
              </button>
            ))}
          </div>
        }
      >
        <div className="rcp-rankings">
          <Ranking
            title={`Above ${labels.py}`}
            other={labels.py}
            rows={ranking}
            list="top_py"
            labels={labels}
          />
          <Ranking
            title={`Above ${labels.plan}`}
            other={labels.plan}
            rows={ranking}
            list="top_plan"
            labels={labels}
          />
          <Ranking
            title={`Below ${labels.py}`}
            other={labels.py}
            rows={ranking}
            list="bottom_py"
            labels={labels}
          />
          <Ranking
            title={`Below ${labels.plan}`}
            other={labels.plan}
            rows={ranking}
            list="bottom_plan"
            labels={labels}
          />
        </div>
      </Card>

      <Card title="End users and their ship-tos" subtitle="Open an end user to see its ship-tos.">
        <TreeTable lines={lines} levels={END_USER_SHIP_TO} first="End user" labels={labels} total={total} />
      </Card>
    </>
  )
}

function Ranking({
  title,
  other,
  rows,
  list,
  labels,
}: {
  title: string
  other: string
  rows: readonly Keyed[]
  list: string
  labels: Labels
}) {
  const picked = rows.filter((row) => text(row['list']) === list)
  const largest = Math.max(1, ...picked.flatMap((row) => [num(row['cy']), num(row['other'])]))
  const totalCy = picked.reduce((s, row) => s + num(row['cy']), 0)
  const totalOther = picked.reduce((s, row) => s + num(row['other']), 0)
  const totalVar = picked.reduce((s, row) => s + num(row['variance']), 0)
  return (
    <div className="rcp-ranking">
      <h3 className="srp-section-title">{title}</h3>
      {picked.length === 0 ? (
        <p className="srp-dim">None.</p>
      ) : (
        <table className="srp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="cell-number">{labels.cy}</th>
              <th className="cell-number">{other}</th>
              <th className="cell-number">Variance</th>
              <th className="cell-number">Var %</th>
              <th aria-label="Bars" />
            </tr>
          </thead>
          <tbody>
            {picked.map((row) => {
              const variance = num(row['variance'])
              return (
                <tr key={`${list}-${String(num(row['rank']))}`}>
                  <td className="srp-wrap">{text(row['name'])}</td>
                  <td className="cell-number">{amount(num(row['cy']), labels.money)}</td>
                  <td className="cell-number">{amount(num(row['other']), labels.money)}</td>
                  <td className={`cell-number ${tone(variance)}`}>{amount(variance, labels.money)}</td>
                  <td className={`cell-number ${tone(variance)}`}>
                    {percent(row['pct'] === null ? null : num(row['pct']))}
                  </td>
                  <td className="rcp-bars" aria-hidden>
                    <span
                      className="rcp-bar rcp-bar-other"
                      style={{
                        width: `${String((num(row['other']) / largest) * 100)}%`,
                      }}
                    />
                    <span
                      className="rcp-bar rcp-bar-cy"
                      style={{
                        width: `${String((num(row['cy']) / largest) * 100)}%`,
                      }}
                    />
                  </td>
                </tr>
              )
            })}
            <tr className="srp-total">
              <td>Total of these {picked.length}</td>
              <td className="cell-number">{amount(totalCy, labels.money)}</td>
              <td className="cell-number">{amount(totalOther, labels.money)}</td>
              <td className={`cell-number ${tone(totalVar)}`}>{amount(totalVar, labels.money)}</td>
              <td className={`cell-number ${tone(totalVar)}`}>
                {percent(totalOther === 0 ? null : (totalVar / Math.abs(totalOther)) * 100)}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      )}
    </div>
  )
}

// --- Detail ----------------------------------------------------------------------------

function Detail({ lines, labels, total }: { lines: readonly Keyed[]; labels: Labels; total: Figures }) {
  const [layout, setLayout] = useState(LAYOUTS[1]?.key ?? 'eu')
  const chosen = LAYOUTS.find((candidate) => candidate.key === layout) ?? LAYOUTS[0]
  if (chosen === undefined) return null
  const first = { sku: 'SKU', end_user: 'End user', product_line: 'Product line', ship_to: 'Ship-to' }[
    chosen.levels[0] ?? 'end_user'
  ]
  return (
    <Card
      title="Detail"
      subtitle="The workbook's pivot tabs in one table. Pick how the rows are grouped; open a row to go a level down."
      actions={
        <div className="rcp-switch" role="group" aria-label="Rows">
          {LAYOUTS.map((candidate) => (
            <button
              key={candidate.key}
              type="button"
              aria-pressed={candidate.key === chosen.key}
              className={candidate.key === chosen.key ? 'srp-chip is-on' : 'srp-chip'}
              onClick={() => {
                setLayout(candidate.key)
              }}
            >
              {candidate.label}
            </button>
          ))}
        </div>
      }
    >
      <TreeTable
        key={chosen.key}
        lines={lines}
        levels={chosen.levels}
        first={first}
        labels={labels}
        total={total}
      />
    </Card>
  )
}

function TreeTable({
  lines,
  levels,
  first,
  labels,
  total,
}: {
  lines: readonly Keyed[]
  levels: Parameters<typeof buildTree>[1]
  first: string
  labels: Labels
  total: Figures
}) {
  const tree = useMemo(() => buildTree(lines, levels), [lines, levels])
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  if (tree.length === 0) return <Empty label="Nothing in these filters" />
  const toggle = (node: TreeNode) => {
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(node.id)) next.delete(node.id)
      else next.add(node.id)
      return next
    })
  }
  const rows: TableRow[] = [
    ...visible(tree, open).map((node) => ({
      id: node.id,
      depth: node.depth,
      figures: node.figures,
      label:
        node.children.length > 0 ? (
          <button
            type="button"
            className="srp-link rcp-toggle"
            aria-expanded={open.has(node.id)}
            onClick={() => {
              toggle(node)
            }}
          >
            <span aria-hidden>{open.has(node.id) ? '▾' : '▸'}</span> {node.label}
          </button>
        ) : (
          <span className="rcp-leaf">{node.label}</span>
        ),
    })),
    { id: 'total', label: 'Grand total', figures: total, total: true },
  ]
  return (
    <div className="srp-tall srp-scroll">
      <CompareTable first={first} rows={rows} labels={labels} />
    </div>
  )
}

// --- Janus -----------------------------------------------------------------------------

function Janus({ result, money }: { result: CustomReportProps['result']; money: boolean }) {
  const shipTos = dataset(result, 'janus')
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const groups = ['5 or more of the 9 SKUs', 'Fewer than 5']
  return (
    <Card
      title="Janus, by customer"
      subtitle="Janus business only (a ship-to and Janus SKU with no January-July 2026 sales), out of every figure above. Opening is each SKU's first shipment at the ship-to; follow-on is every later shipment of it -- how the team is comped."
    >
      {shipTos.length === 0 ? (
        <Empty label="No Janus business in these filters" />
      ) : (
        groups.map((group) => {
          const inGroup = shipTos.filter((row) => text(row['taking']) === group)
          if (inGroup.length === 0) return null
          const customers = new Map<string, Keyed[]>()
          for (const row of inGroup) {
            const name = text(row['end_user']) ?? '(no end user)'
            customers.set(name, [...(customers.get(name) ?? []), row])
          }
          return (
            <Fragment key={group}>
              <h3 className="srp-section-title">
                {group === groups[0]
                  ? 'Taking the line: 5 or more of the 9 SKUs'
                  : 'Fewer than 5 of the 9 SKUs'}
              </h3>
              <div className="srp-scroll">
                <table className="srp-table">
                  <thead>
                    <tr>
                      <th>Customer / ship-to</th>
                      <th className="cell-number">Ship-tos</th>
                      <th className="cell-number">SKUs</th>
                      <th>First shipped</th>
                      <th className="cell-number">Opening</th>
                      <th className="cell-number">Follow-on</th>
                      <th className="cell-number">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...customers.entries()].map(([name, rows]) => {
                      const id = `${group}/${name}`
                      const isOpen = open.has(id)
                      const firsts = rows
                        .map((row) => text(row['first_shipped']))
                        .filter((d): d is string => d !== null)
                        .sort()
                      return (
                        <Fragment key={id}>
                          <tr>
                            <td className="srp-wrap">
                              <button
                                type="button"
                                className="srp-link rcp-toggle"
                                aria-expanded={isOpen}
                                onClick={() => {
                                  setOpen((current) => {
                                    const next = new Set(current)
                                    if (next.has(id)) next.delete(id)
                                    else next.add(id)
                                    return next
                                  })
                                }}
                              >
                                <span aria-hidden>{isOpen ? '▾' : '▸'}</span> {name}
                              </button>
                            </td>
                            <td className="cell-number">{rows.length}</td>
                            <td className="cell-number" />
                            <td>{monthName(firsts[0] ?? null)}</td>
                            <td className="cell-number">
                              {amount(
                                rows.reduce((s, r) => s + num(r['opening']), 0),
                                money,
                              )}
                            </td>
                            <td className="cell-number">
                              {amount(
                                rows.reduce((s, r) => s + num(r['follow_on']), 0),
                                money,
                              )}
                            </td>
                            <td className="cell-number srp-strong">
                              {amount(
                                rows.reduce((s, r) => s + num(r['total']), 0),
                                money,
                              )}
                            </td>
                          </tr>
                          {isOpen &&
                            rows.map((row) => (
                              <tr key={`${id}/${text(row['ship_to']) ?? ''}`}>
                                <td className="srp-wrap" style={{ paddingLeft: '1.45rem' }}>
                                  {text(row['ship_to_customer']) ?? text(row['ship_to'])} (
                                  {text(row['ship_to'])})
                                </td>
                                <td className="cell-number" />
                                <td className="cell-number">{num(row['skus'])} of 9</td>
                                <td>{monthName(text(row['first_shipped']))}</td>
                                <td className="cell-number">{amount(num(row['opening']), money)}</td>
                                <td className="cell-number">{amount(num(row['follow_on']), money)}</td>
                                <td className="cell-number">{amount(num(row['total']), money)}</td>
                              </tr>
                            ))}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Fragment>
          )
        })
      )}
    </Card>
  )
}
